import {useEffect, useMemo, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {Accordion} from '@base-ui/react/accordion'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import PolymorphicElement from '@s-ui/react-primitive-polymorphic-element'
import VisuallyHidden from '@s-ui/react-primitive-visually-hidden'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconChevronUp from '@adv-mt/mt-svg-icons/lib/ChevronUp.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import useFilterGroups from './hooks/useFilterGroups.js'
import {BASE_CLASS_NAME, FILTERS_OPENED_BY_DEFAULT} from './config.js'
import {MoleculeAccordionLabel} from './molecule-accordion-label.js'

const FiltersGroupedByAccordionsExperiment = ({filters, listingSectionId, overlayContentRef, searchTags}) => {
  const [accordionsOpenedByDefault, setAccordionsOpenedByDefault] = useState(FILTERS_OPENED_BY_DEFAULT)

  const {groups} = useFilterGroups({
    filters,
    listingSectionId,
    searchTags,
    accordionsOpenedByDefault
  })
  const {track} = useAnalyticsTrack()

  const delayToNextFrame = useDelayToNextFrame()

  const defaultOpenedAccordions = useMemo(
    () => groups.filter(({count, isOpenedByDefault}) => count || isOpenedByDefault).map(({groupId}) => groupId),
    [groups]
  )

  const [openedAccordions, setOpenedAccordions] = useState(defaultOpenedAccordions)
  const prevOpenedRef = useRef(openedAccordions)

  const handleValueChange = newValues => {
    const prev = prevOpenedRef.current
    const added = newValues.find(v => !prev.includes(v))
    const removed = prev.find(v => !newValues.includes(v))
    const changedValue = added ?? removed
    const isExpanded = Boolean(added)

    prevOpenedRef.current = newValues
    setOpenedAccordions(newValues)

    delayToNextFrame(() => {
      isExpanded &&
        track({
          event: 'Filter Block Opened',
          data: {
            hit_information: changedValue
          }
        })
    })
    if (changedValue && FILTERS_OPENED_BY_DEFAULT[changedValue] !== undefined) {
      setAccordionsOpenedByDefault({...accordionsOpenedByDefault, [changedValue]: isExpanded})
    }
  }

  const filtersKey = useMemo(() => filters?.map(f => f.name).join(',') ?? '', [filters])

  useEffect(() => {
    prevOpenedRef.current = defaultOpenedAccordions
    setOpenedAccordions(defaultOpenedAccordions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey])

  return (
    <Accordion.Root multiple value={openedAccordions} onValueChange={handleValueChange} className={BASE_CLASS_NAME}>
      {groups.map(({filters, count, groupId, icon, label}) => {
        const hasMoreThanOneFilter = filters.length > 1
        const isOpen = openedAccordions.includes(groupId)
        return (
          <Accordion.Item className={`${BASE_CLASS_NAME}-group`} key={groupId} id={groupId} value={groupId}>
            <Accordion.Header render={<div />}>
              <Accordion.Trigger className={`${BASE_CLASS_NAME}-groupTrigger`}>
                <MoleculeAccordionLabel label={label} count={count} groupId={groupId} icon={icon} />
                <div className={`${BASE_CLASS_NAME}-accordionHeaderIcon`}>
                  {isOpen ? (
                    <IconChevronUp size={ATOM_ICON_SIZES.medium} />
                  ) : (
                    <IconChevronDown size={ATOM_ICON_SIZES.medium} />
                  )}
                </div>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Panel className={`${BASE_CLASS_NAME}-groupPanel`}>
              <PolymorphicElement
                as={hasMoreThanOneFilter ? 'fieldset' : 'div'}
                className={`${BASE_CLASS_NAME}-groupItem`}
              >
                {hasMoreThanOneFilter && <VisuallyHidden as="legend">{label}</VisuallyHidden>}
                {filters.map(
                  ({component: Filter, ...props}, index) =>
                    Filter && (
                      <Filter
                        key={`${groupId}-${index}`}
                        {...props}
                        overlayContentRef={overlayContentRef}
                        withLabel={filters.length > 1}
                      />
                    )
                )}
              </PolymorphicElement>
            </Accordion.Panel>
          </Accordion.Item>
        )
      })}
    </Accordion.Root>
  )
}

FiltersGroupedByAccordionsExperiment.displayName = 'FiltersGroupedByAccordionsExperiment'

FiltersGroupedByAccordionsExperiment.propTypes = {
  filters: PropTypes.array,
  listingSectionId: PropTypes.string,
  overlayContentRef: PropTypes.node,
  searchTags: PropTypes.array
}

export default FiltersGroupedByAccordionsExperiment
